import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"

import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { type BasePagination } from "../../../../../types/api.ts"
import { DATE_FORMAT } from "../../../../../utils/date/date.ts"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../../../Branches/redux/branches.slice.ts"
import * as teamServices from "../../../../TeamManagementPage/api/services.ts"
import { USER_STATUSES } from "../../../../TeamManagementPage/constants/user-statuses.ts"
import { type TeamMember } from "../../../../TeamManagementPage/types/types.ts"
import * as calendarServices from "../../../api/services.ts"
import { type CalendarFilterParticipant } from "../../../redux/calendar.types.ts"
import { type Event } from "../../../types/types.ts"

// A team large enough to need real pagination here is future work — this
// covers every account this view has actually been checked against.
const ROSTER_PAGE_SIZE = 100

interface AvailabilityMember {
  id: string
  name: string
}

interface UseTeamAvailabilityEventsResult {
  members: AvailabilityMember[]
  eventsByMemberId: Record<string, Event[]>
  loading: boolean
  // Distinct from "loaded, nobody has anything today" — a failed fetch must
  // read as an error, not as a team that's silently all free.
  error: boolean
  // EAD-1293: this view used to drop invited-but-not-yet-accepted members
  // with no trace, so "No team members available" read as if the team were
  // literally empty rather than "everyone is still pending". 0 whenever a
  // Team filter is active — a filter already names exactly who to show, and
  // "pending" has no meaning against an explicit selection.
  pendingCount: number
}

const memberName = (member: TeamMember) =>
  [member.first_name, member.last_name].filter(Boolean).join(" ").trim() ||
  member.email

export function useTeamAvailabilityEvents({
  enabled,
  date,
  timezone,
  filterParticipants
}: {
  // False for the solo fallback (no `calendar_view_all` and no Team filter)
  // — there's nothing this hook could fetch that view is allowed to see
  // anyway, so it does neither fetch at all rather than run them pointlessly.
  enabled: boolean
  date: Date
  timezone: string
  filterParticipants: CalendarFilterParticipant[] | null
}): UseTeamAvailabilityEventsResult {
  const hasFilter = !!filterParticipants?.length

  // The roster itself (who counts as "the team" this view shows) respects
  // the Branches filter — a single branch workspace's own scope, or
  // Company HQ's multi-select — same precedence as everywhere else this
  // session wired it. The events fetch below is deliberately NOT
  // branch-scoped: a shared employee's meeting in another branch must
  // still make them read as busy here, not silently available.
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const pageBranchScope = useAppSelector(selectPageFilter("calendar"))
  const rosterTeamIds = !isCompanyMode
    ? activeBranchId
    : pageBranchScope.type === "subset"
      ? pageBranchScope.teamIds.join(",")
      : null

  const [roster, setRoster] = useState<AvailabilityMember[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [rosterError, setRosterError] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)

  // Only fetched when nothing narrower is already selected — a Team filter
  // already names exactly who to show, so fetching the whole roster too
  // would be wasted work every time this view mounts.
  useEffect(() => {
    if (!enabled || hasFilter) {
      setPendingCount(0)
      return
    }
    let cancelled = false
    setRosterLoading(true)
    setRosterError(false)

    teamServices
      .getUsers({
        status: USER_STATUSES.ACTIVE,
        page_size: ROSTER_PAGE_SIZE,
        team_ids: rosterTeamIds
      })
      .then((response) => {
        if (cancelled) return
        if (!response.ok) {
          setRosterError(true)
          setRoster([])
          return
        }
        const results =
          (response.data as BasePagination<TeamMember>)?.results || []
        setRoster(results.map((m) => ({ id: m.id, name: memberName(m) })))
      })
      .finally(() => {
        if (!cancelled) setRosterLoading(false)
      })

    // A count only — never rendered as rows, so page_size: 1 is enough.
    // Failure here is silent on purpose: a pending-invite count is an
    // explanatory footnote, not something worth surfacing this view's one
    // error state over.
    teamServices
      .getUsers({
        status: USER_STATUSES.INVITE_SENT,
        page_size: 1,
        team_ids: rosterTeamIds
      })
      .then((response) => {
        if (cancelled || !response.ok) return
        setPendingCount(
          (response.data as BasePagination<TeamMember>)?.count || 0
        )
      })

    return () => {
      cancelled = true
    }
  }, [enabled, hasFilter, rosterTeamIds])

  const members: AvailabilityMember[] = useMemo(() => {
    if (!enabled) return []
    return hasFilter
      ? (filterParticipants || []).map((p) => ({ id: p.value, name: p.label }))
      : roster
  }, [enabled, hasFilter, filterParticipants, roster])

  const memberIdsKey = useMemo(
    () =>
      members
        .map((m) => m.id)
        .sort()
        .join(","),
    [members]
  )

  const dayKey = dayjs(date).tz(timezone).format(DATE_FORMAT)

  const [events, setEvents] = useState<Event[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsError, setEventsError] = useState(false)

  useEffect(() => {
    if (!memberIdsKey) {
      setEvents([])
      setEventsLoading(false)
      setEventsError(false)
      return
    }

    let cancelled = false
    setEventsLoading(true)
    setEventsError(false)

    const dayStart = dayjs.tz(dayKey, DATE_FORMAT, timezone).startOf("day")
    const dayEnd = dayStart.endOf("day")

    calendarServices
      .getEvents({
        start: dayStart.format(),
        end: dayEnd.format(),
        participants: memberIdsKey
      })
      .then((response) => {
        if (cancelled) return
        if (!response.ok) {
          setEventsError(true)
          setEvents([])
          return
        }
        setEvents(response.data?.results || [])
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [memberIdsKey, dayKey, timezone])

  const eventsByMemberId = useMemo(() => {
    const map: Record<string, Event[]> = {}
    members.forEach((m) => {
      map[m.id] = []
    })

    events.forEach((event) => {
      // The list endpoint nests created_by/participants (and lead/project/
      // category/contact) under extended_props rather than on the event
      // itself — confirmed against the real response, not just the Event
      // type's declared shape. Reading them at the top level silently
      // matched nothing, which is why every member showed "Available" even
      // on a day with real meetings.
      const createdBy = event.extended_props?.created_by ?? event.created_by
      const participants =
        event.extended_props?.participants ?? event.participants

      const ids = new Set<string>()
      if (createdBy?.id) ids.add(createdBy.id)
      participants?.forEach((p) => {
        if (p?.id) ids.add(p.id)
      })

      ids.forEach((id) => {
        if (map[id]) map[id].push(event)
      })
    })

    return map
  }, [events, members])

  return {
    members,
    eventsByMemberId,
    // Unfiltered: gated on the roster alone, not the events fetch too —
    // otherwise the member list would flash in as soon as the roster
    // resolves and then vanish again behind the spinner the moment the
    // (separate, later) events fetch starts. Filtered: there's no roster
    // fetch at all (members come straight from filterParticipants), so the
    // only real in-flight work is the events fetch itself — reporting
    // `false` here unconditionally let a filtered member's row render
    // "Available" before their events had actually loaded.
    loading: hasFilter ? eventsLoading : rosterLoading,
    error: (hasFilter ? false : rosterError) || eventsError,
    pendingCount
  }
}
